import { getItem } from './localStorageManagement';
import { REFERRAL_STORAGE_KEY } from './referral';
import { withUtm } from './tracking';

/**
 * Whether this browser is signed in to the portal.
 *
 * The portal lives on the same origin as this site (younet.ai/portal), and
 * keeps its session in localStorage (`token`, `refreshToken` — the portal's
 * _helpers/tokenManagement.js; helpers/auth.js here clears the same keys).
 * A landing page uses this only to choose where a CTA should go: a signed-in
 * visitor straight to the product, anyone else to sign-up. It is a hint, not
 * a security check — the portal decides what a visitor may see.
 */
export const hasPortalSession = () => {
    try {
        return Boolean(window.localStorage.getItem('token') || window.localStorage.getItem('refreshToken'));
    } catch (error) {
        return false; // storage blocked: treat as signed out, sign-up still works
    }
};

/**
 * A portal path, reached the right way for this visitor.
 *
 * @param {string} path  a portal router path, e.g. "/tasks-cloud?view=templates"
 */
export const PORTAL_URL = 'https://younet.ai/portal';

/** The portal's quiz without a persona: its first step is the persona picker. */
export const START_URL = `${PORTAL_URL}/start`;

/**
 * The quiz URL (YA-1237): `/portal/start` or `/portal/start/<persona>`,
 * carrying the visit's campaign params (utm_* and the ad click ids, via
 * withUtm) and the referral code. `ref` is stripped from the address bar on
 * arrival (helpers/referral.js) and lives in localStorage, so it is read back
 * from there — the portal shares the origin and reads the same key, but the
 * quiz stores it with its answers, so it is sent along explicitly.
 * Every sign-up pill of the site goes here; the portal's /start redirects to
 * the plain sign-up by itself while the funnel is switched off server-side.
 */
export const startHref = (search = '', persona = null) => {
    const params = new URLSearchParams();
    if (persona) params.set('persona', persona);
    const ref = getItem(REFERRAL_STORAGE_KEY);
    if (ref) params.set('ref', ref);
    const query = params.toString();
    return withUtm(`${START_URL}${persona ? `/${encodeURIComponent(persona)}` : ''}${query ? `?${query}` : ''}`, search);
};

/**
 * Signed in: the path itself. Signed out: the quiz (YA-1237) — the account is
 * created at its end and the setup lands the visitor in their first job, so
 * the deep link is not carried: the quiz picks the job.
 */
export const portalEntry = (path, signedIn) => (signedIn ? `${PORTAL_URL}${path}` : startHref());
